import { CSSProperties, ChangeEvent, KeyboardEvent, forwardRef, useCallback, useMemo, useState } from "react";

import { LoadingGroup } from "../../general";
import { createComponentStyleHook } from "../../tokens";
import classNames from "classnames";

import { useId } from "../../hooks/useId";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { Overlay } from "../Textbox/Overlay";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { NumberTextboxProps } from "./NumberTextbox";
import { Placeholder } from "./Placeholder";
import { StyledNativeInput } from "./StyledNativeInput";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedNumberTextboxStyles = createComponentStyleHook<InputProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "textbox",
});

/**
 * The `Input` component properties.
 */
export interface InputProps extends NumberTextboxProps {}

/**
 * The base input for number textboxes.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    label,
    tooltip,
    instructions,
    hideInstructions,
    hideLabel,
    defaultValue = null,
    required = false,
    autoFocus,
    tabIndex,
    step = 1,
    min,
    max,
    inputMode = "numeric",
    prefix,
    suffix,
    onChange,
    onFocusEnter,
    onFocusExit,
    onEnter,
  } = props;

  const { value = defaultValue } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedNumberTextboxStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Textbox, "number-textbox"],
    testIdPrefix: "number-textbox",
  });

  const id = useId();
  const errorId = `${id}-error`;
  const labelId = `${id}-label`;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();
  const [prefixSize, setPrefixSize] = useState<number | null>(null);
  const [suffixSize, setSuffixSize] = useState<number | null>(null);
  const actualInstructions = useMemo(() => {
    if (instructions) return instructions;
    if (min !== undefined && max !== undefined) return `Must be between ${min} and ${max}`;
    if (min !== undefined) return `Must be greater than or equal to ${min}`;
    if (max !== undefined) return `Must be less than or equal to ${max}`;
    return undefined;
  }, [instructions, min, max]);
  const instructionsId = actualInstructions ? `${id}-instructions` : undefined;

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (disabled || readOnly || submitting) return;

      const rawValue = ev.target.value;
      const numericValue = rawValue === "" ? null : Number(rawValue);

      onChange?.(numericValue, value);
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const keyDownHandler = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === "Enter" && onEnter) {
        ev.preventDefault();
        onEnter(value);
      }
    },
    [value, onEnter],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
  }, [onFocusExit]);

  const wheelHandler = useCallback((ev: React.WheelEvent<HTMLInputElement>) => {
    ev.currentTarget.blur();
  }, []);

  const overlayLayoutHandler = useCallback((prefixSize: number | null, suffixSize: number | null) => {
    setPrefixSize(prefixSize);
    setSuffixSize(suffixSize);
  }, []);

  const ariaDescribedBy = [instructionsId, invalid ? errorId : undefined].filter(Boolean).join(" ") || undefined;

  const getAriaLabel = () => {
    if (!hideLabel) return undefined;
    if (typeof label === "string") return label;
    return "Number input";
  };

  const style = useMemo(() => {
    if (prefixSize === null && suffixSize === null) return {};
    const result: CSSProperties = {};
    if (prefixSize !== null) result.paddingLeft = `${prefixSize}px`;
    if (suffixSize !== null) result.paddingRight = `${suffixSize}px`;
    return result;
  }, [prefixSize, suffixSize]);

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder
        fallback={<Placeholder className={props.className} style={props.style} hasInstructions={!!actualInstructions} />}
      >
        <InputContainer
          testId={baseHtmlProps["data-testid"]}
          className={baseHtmlProps.className}
          style={baseHtmlProps.style}
          id={id}
          label={label}
          instructions={actualInstructions}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tooltip={tooltip}
          hideInstructions={hideInstructions}
          hideLabel={hideLabel}
        >
          <div className="input-container">
            <StyledNativeInput
              aria-busy={submitting || undefined}
              aria-describedby={ariaDescribedBy}
              aria-errormessage={invalid ? errorId : undefined}
              aria-invalid={invalid || undefined}
              aria-label={getAriaLabel()}
              aria-labelledby={!hideLabel ? labelId : undefined}
              aria-readonly={readOnly || submitting}
              aria-required={disabled || readOnly ? undefined : required}
              autoFocus={autoFocus}
              className={classNames("input", { invalid })}
              data-testid={baseHtmlProps["data-testid"]}
              disabled={disabled}
              id={id}
              inputMode={inputMode}
              onBlur={blurHandler}
              onChange={changeHandler}
              onKeyDown={keyDownHandler}
              onFocus={focusHandler}
              onWheel={wheelHandler}
              style={style}
              readOnly={readOnly || submitting}
              ref={ref}
              step={step}
              tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
              type="number"
              value={value ?? ""}
            />
            <Overlay prefix={prefix} suffix={suffix} onLayout={overlayLayoutHandler} />
          </div>
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
